import { useEffect } from 'react'
import { isEqual } from 'lodash'
import { convertTraits } from '@aytm/app/stats/lib/traits'

import { decodeText } from '@aytm/app/stats/lib/helpers'
import { mapReportSearchQuery, getTagsList, getIsDefaultReportParams } from '@aytm/app/stats/page/comments/lib/utils'
import {
  DEFAULT_ORDER_BY,
  DEFAULT_PER_PAGE_IMAGE_RESPONSES,
  DEFAULT_SELECTED_QUESTION,
  ORDER_BY_OPTIONS_IMAGES
} from '@aytm/app/stats/page/comments/lib/constants'

const useComments = ({
  dispatch,
  searchStateRef,
  reportParams,
  propsRef,
  responsesOptionsRef,
  survey,
  surveyData,
  specialMode,
  questionsList,
  responsesRef,
  reportVirtual,
  reportSchema,
  pageData,
  setSurvey,
  setResponsesOptions,
  onQuestionsListChange,
  onUpdateSchema
}) => {
  useEffect(() => {
    const traits = convertTraits(surveyData.survey || {})
    const _surveyData = Object.assign({}, surveyData.survey || {}, {
      traits,
      codeBooks: (surveyData.survey || {}).codeBooks || []
    })

    if (!isEqual(survey, _surveyData)) {
      setSurvey(_surveyData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyData])

  useEffect(() => {
    if (onQuestionsListChange && 'function' == typeof onQuestionsListChange) {
      onQuestionsListChange(questionsList)
    }
  }, [questionsList, onQuestionsListChange])

  useEffect(() => {
    const selectedOrderBy =
      reportParams.orderBy && ORDER_BY_OPTIONS_IMAGES.find(item => item.value == reportParams.orderBy)
    const question =
      reportParams.selectedQuestion && reportParams.selectedQuestion !== DEFAULT_SELECTED_QUESTION
        ? reportParams.selectedQuestion
        : null
    const pageSize = reportParams.pageSize || DEFAULT_PER_PAGE_IMAGE_RESPONSES
    const isFailedOrderBy =
      selectedOrderBy &&
      (('similarity' == selectedOrderBy.value && !question) ||
        ('similarity' == selectedOrderBy.value && window.features?.oe_sort_similarity?.status !== 'on'))

    const request = {
      query: searchStateRef.current.request.query,
      currentPage: searchStateRef.current.request.currentPage,
      selectedQuestion: reportParams.selectedQuestion || DEFAULT_SELECTED_QUESTION,
      pageSize,
      orderBy: !reportParams.orderBy || isFailedOrderBy ? DEFAULT_ORDER_BY : reportParams.orderBy,
      orderDirection: !reportParams.orderDirection || isFailedOrderBy ? 'asc' : reportParams.orderDirection
    }

    const reportQuery = reportParams.query
      ? reportParams.query
      : reportParams.searchString
      ? [
          {
            value: decodeText(reportParams.searchString, false),
            label: decodeText(reportParams.searchString, false),
            type: 'plain'
          }
        ]
      : []

    if (!isEqual(searchStateRef.current.request.query, mapReportSearchQuery(reportQuery))) {
      request.query = mapReportSearchQuery(reportQuery)

      dispatch({ type: 'SET_PARAM', request: { name: 'totalPages', value: 0 } })
    } else if (searchStateRef.current.request.pageSize != pageSize) {
      const newTotalPages = Math.ceil(searchStateRef.current.totalImageResponses / pageSize)

      dispatch({ type: 'SET_PARAM', request: { name: 'totalPages', value: newTotalPages } })
    }

    dispatch({
      type: 'SET_REQUEST',
      request: JSON.parse(JSON.stringify(request))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportParams])

  useEffect(() => {
    if (!searchStateRef.current.isProcessing) return

    responsesRef.current = []

    setResponsesOptions({
      ...responsesOptionsRef.current,
      survey,
      reportVirtual,
      reportSchema
    })

    dispatch({
      type: 'SET_PARAMS',
      request: {
        value: { tagsList: getTagsList(survey.tags, { codeBooks: survey.codeBooks }), isProcessing: false }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey, reportVirtual, reportSchema])

  useEffect(() => {
    responsesRef.current = []

    responsesOptionsRef.current.pageData = pageData
    setResponsesOptions({ ...responsesOptionsRef.current })
    onUpdateSchema({ cs: pageData }, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData])

  useEffect(() => {
    if (!propsRef.current.onUpdateParams) return

    const params = structuredClone(searchStateRef.current.request)
    delete params.currentPage // the page is navigation, not report state - nothing restores it
    propsRef.current.onUpdateParams(
      getIsDefaultReportParams(params, specialMode) ? null : params,
      undefined,
      specialMode
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchStateRef.current.request, specialMode])
}

export default useComments
